import React, { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { LogOut, Plus, Pencil, Trash2, Mail, BookOpen, Users, Image as ImageIcon, FileText, User } from "lucide-react";

/* ============= Layout wrapper ============= */
export default function Admin() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <p className="p-10 text-[#8A817C]">Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;

  const items = [
    { to: "/admin", label: "Dashboard", icon: FileText, end: true },
    { to: "/admin/profile", label: "Profile", icon: User },
    { to: "/admin/publications", label: "Publications", icon: BookOpen },
    { to: "/admin/activities", label: "Activities", icon: Users },
    { to: "/admin/gallery", label: "Gallery", icon: ImageIcon },
    { to: "/admin/blog", label: "Blog", icon: FileText },
    { to: "/admin/messages", label: "Messages", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#F5EFE6] flex">
      <aside data-testid="admin-sidebar" className="w-64 bg-white border-r border-[#D8CFC4] p-6 flex flex-col">
        <Link to="/" className="font-serif-display text-xl text-[#1A2456]">Dr. Ruchi Singh</Link>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#C24D2C] mt-1">Admin</p>
        <nav className="mt-8 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded transition-colors ${
                  isActive ? "bg-[#E35D29] text-white" : "text-[#2D2825] hover:bg-[#F5EFE6]"
                }`
              }
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid="admin-logout"
          onClick={async () => {
            await logout();
            nav("/");
          }}
          className="mt-auto flex items-center gap-2 text-sm text-[#C24D2C] hover:text-[#E35D29]"
        >
          <LogOut size={16} /> Logout
        </button>
      </aside>

      <main className="flex-1 p-8 lg:p-10 overflow-x-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="profile" element={<ProfileEditor />} />
          <Route path="publications" element={<PublicationsAdmin />} />
          <Route path="activities" element={<ActivitiesAdmin />} />
          <Route path="gallery" element={<GalleryAdmin />} />
          <Route path="blog" element={<BlogAdmin />} />
          <Route path="messages" element={<MessagesAdmin />} />
        </Routes>
      </main>
    </div>
  );
}

/* ============= Dashboard ============= */
function Dashboard() {
  const [counts, setCounts] = useState({ pubs: 0, acts: 0, gallery: 0, blog: 0, msgs: 0, unread: 0 });
  useEffect(() => {
    (async () => {
      try {
        const [p, a, g, b, m] = await Promise.all([
          api.get("/publications"),
          api.get("/activities"),
          api.get("/gallery"),
          api.get("/blog"),
          api.get("/messages"),
        ]);
        setCounts({
          pubs: p.data.length,
          acts: a.data.length,
          gallery: g.data.length,
          blog: b.data.length,
          msgs: m.data.length,
          unread: m.data.filter((x) => !x.is_read).length,
        });
      } catch {}
    })();
  }, []);
  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif-display text-3xl text-[#1A2456]">Dashboard</h1>
      <p className="text-sm text-[#5C544F] mt-1">Overview of everything on your website.</p>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          ["Publications", counts.pubs],
          ["Activities", counts.acts],
          ["Gallery", counts.gallery],
          ["Blog Posts", counts.blog],
          ["Messages", counts.msgs],
          ["Unread", counts.unread, true],
        ].map(([label, val, alert]) => (
          <Card key={label} className="p-5 bg-white border-[#D8CFC4] rounded">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#8A817C]">{label}</p>
            <p className={`mt-2 font-serif-display text-3xl ${alert && val > 0 ? "text-[#E35D29]" : "text-[#1A2456]"}`}>
              {val}
            </p>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ============= Profile Editor ============= */
function ProfileEditor() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/profile").then((r) => setP(r.data)); }, []);
  if (!p) return <p>Loading…</p>;
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  const toList = (v) => (typeof v === "string" ? v.split("\n").map((s) => s.trim()).filter(Boolean) : v || []);
  const save = async () => {
    try {
      const payload = {
        ...p,
        research_interests: toList(p.research_interests),
        awards: toList(p.awards),
      };
      await api.put("/profile", payload);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const ri = Array.isArray(p.research_interests) ? p.research_interests.join("\n") : p.research_interests || "";
  const aw = Array.isArray(p.awards) ? p.awards.join("\n") : p.awards || "";

  return (
    <div data-testid="admin-profile" className="max-w-3xl">
      <h1 className="font-serif-display text-3xl text-[#1A2456]">Profile</h1>
      <div className="mt-6 grid gap-4 bg-white p-6 border border-[#D8CFC4] rounded">
        {[
          ["name", "Name"],
          ["title", "Title"],
          ["institution", "Institution"],
          ["photo_url", "Photo URL"],
          ["email", "Email"],
          ["phone", "Phone"],
          ["address", "Address"],
          ["orcid", "ORCID"],
          ["linkedin", "LinkedIn"],
          ["google_scholar", "Google Scholar"],
        ].map(([k, l]) => (
          <div key={k}>
            <Label>{l}</Label>
            <Input data-testid={`profile-${k}`} value={p[k] || ""} onChange={set(k)} />
          </div>
        ))}
        <div>
          <Label>Bio</Label>
          <Textarea data-testid="profile-bio" rows={5} value={p.bio || ""} onChange={set("bio")} />
        </div>
        <div>
          <Label>Research Interests (one per line)</Label>
          <Textarea data-testid="profile-interests" rows={5} value={ri} onChange={(e) => setP({ ...p, research_interests: e.target.value })} />
        </div>
        <div>
          <Label>Awards & Recognitions (one per line)</Label>
          <Textarea data-testid="profile-awards" rows={6} value={aw} onChange={(e) => setP({ ...p, awards: e.target.value })} />
        </div>
        <div>
          <Label>Experience</Label>
          <Textarea data-testid="profile-experience" rows={3} value={p.experience || ""} onChange={set("experience")} />
        </div>
        <Button data-testid="profile-save" onClick={save} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white rounded w-fit">
          Save Profile
        </Button>
      </div>
    </div>
  );
}

/* ============= Publications ============= */
function PublicationsAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/publications").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const empty = { title: "", authors: "", journal: "", year: "", link: "", abstract: "" };
  const save = async (data) => {
    const payload = { ...data, year: data.year ? parseInt(data.year) : null };
    try {
      if (data.id) await api.put(`/publications/${data.id}`, payload);
      else await api.post("/publications", payload);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this publication?")) return;
    await api.delete(`/publications/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-publications">
      <div className="flex justify-between items-center">
        <h1 className="font-serif-display text-3xl text-[#1A2456]">Publications</h1>
        <Button data-testid="add-publication-btn" onClick={() => setEdit(empty)} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white rounded">
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>
      <Card className="mt-6 bg-white border-[#D8CFC4] rounded overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Year</TableHead>
              <TableHead>Journal</TableHead>
              <TableHead className="w-32">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`pub-row-${p.id}`}>
                <TableCell className="font-medium">{p.title}</TableCell>
                <TableCell>{p.year}</TableCell>
                <TableCell>{p.journal}</TableCell>
                <TableCell className="flex gap-2">
                  <button data-testid={`pub-edit-${p.id}`} onClick={() => setEdit(p)} className="text-[#1A2456] hover:text-[#E35D29]"><Pencil size={16} /></button>
                  <button data-testid={`pub-delete-${p.id}`} onClick={() => remove(p.id)} className="text-[#C24D2C] hover:text-red-700"><Trash2 size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <EditorDialog
        open={!!edit}
        onOpenChange={(o) => !o && setEdit(null)}
        title={edit?.id ? "Edit Publication" : "New Publication"}
        data={edit}
        fields={[
          ["title", "Title", "input"],
          ["authors", "Authors", "input"],
          ["journal", "Journal", "input"],
          ["year", "Year", "input"],
          ["link", "Link", "input"],
          ["abstract", "Abstract", "textarea"],
        ]}
        onSave={save}
      />
    </div>
  );
}

/* ============= Activities ============= */
function ActivitiesAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/activities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const empty = { title: "", description: "", location: "", date: "", image_url: "" };
  const save = async (data) => {
    try {
      if (data.id) await api.put(`/activities/${data.id}`, data);
      else await api.post("/activities", data);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    await api.delete(`/activities/${id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="admin-activities">
      <div className="flex justify-between items-center">
        <h1 className="font-serif-display text-3xl text-[#1A2456]">Activities</h1>
        <Button data-testid="add-activity-btn" onClick={() => setEdit(empty)} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white rounded">
          <Plus size={16} className="mr-1" /> Add
        </Button>
      </div>
      <Card className="mt-6 bg-white border-[#D8CFC4] rounded overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Date</TableHead><TableHead>Location</TableHead><TableHead className="w-32">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((a) => (
              <TableRow key={a.id} data-testid={`act-row-${a.id}`}>
                <TableCell className="font-medium">{a.title}</TableCell>
                <TableCell>{a.date}</TableCell>
                <TableCell>{a.location}</TableCell>
                <TableCell className="flex gap-2">
                  <button data-testid={`act-edit-${a.id}`} onClick={() => setEdit(a)} className="text-[#1A2456] hover:text-[#E35D29]"><Pencil size={16} /></button>
                  <button data-testid={`act-delete-${a.id}`} onClick={() => remove(a.id)} className="text-[#C24D2C]"><Trash2 size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <EditorDialog
        open={!!edit}
        onOpenChange={(o) => !o && setEdit(null)}
        title={edit?.id ? "Edit Activity" : "New Activity"}
        data={edit}
        fields={[
          ["title", "Title", "input"],
          ["date", "Date (e.g., June 2025)", "input"],
          ["location", "Location", "input"],
          ["image_url", "Image URL", "input"],
          ["description", "Description", "textarea"],
        ]}
        onSave={save}
      />
    </div>
  );
}

/* ============= Gallery ============= */
function GalleryAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ image_url: "", caption: "" });
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.image_url) return toast.error("Image URL required");
    try {
      await api.post("/gallery", form);
      toast.success("Added");
      setForm({ image_url: "", caption: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    await api.delete(`/gallery/${id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="admin-gallery">
      <h1 className="font-serif-display text-3xl text-[#1A2456]">Gallery</h1>
      <Card className="mt-6 p-6 bg-white border-[#D8CFC4] rounded">
        <div className="grid md:grid-cols-3 gap-3">
          <Input data-testid="gallery-image-url" placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          <Input data-testid="gallery-caption" placeholder="Caption (optional)" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
          <Button data-testid="gallery-add-btn" onClick={add} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white rounded"><Plus size={16} className="mr-1" /> Add photo</Button>
        </div>
      </Card>
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} data-testid={`gallery-item-${g.id}`} className="relative group">
            <img src={g.image_url} alt={g.caption} className="w-full aspect-square object-cover" />
            <button
              data-testid={`gallery-delete-${g.id}`}
              onClick={() => remove(g.id)}
              className="absolute top-2 right-2 bg-white/90 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Trash2 size={14} className="text-[#C24D2C]" />
            </button>
            {g.caption && <p className="mt-2 text-xs text-[#5C544F]">{g.caption}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============= Blog ============= */
function BlogAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const empty = { title: "", content: "", excerpt: "", image_url: "" };
  const save = async (data) => {
    try {
      if (data.id) await api.put(`/blog/${data.id}`, data);
      else await api.post("/blog", data);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/blog/${id}`);
    toast.success("Deleted"); load();
  };
  return (
    <div data-testid="admin-blog">
      <div className="flex justify-between items-center">
        <h1 className="font-serif-display text-3xl text-[#1A2456]">Blog</h1>
        <Button data-testid="add-blog-btn" onClick={() => setEdit(empty)} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white rounded"><Plus size={16} className="mr-1" /> Add</Button>
      </div>
      <Card className="mt-6 bg-white border-[#D8CFC4] rounded overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Created</TableHead><TableHead className="w-32">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((b) => (
              <TableRow key={b.id} data-testid={`blog-row-${b.id}`}>
                <TableCell className="font-medium">{b.title}</TableCell>
                <TableCell>{new Date(b.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="flex gap-2">
                  <button data-testid={`blog-edit-${b.id}`} onClick={() => setEdit(b)} className="text-[#1A2456]"><Pencil size={16} /></button>
                  <button data-testid={`blog-delete-${b.id}`} onClick={() => remove(b.id)} className="text-[#C24D2C]"><Trash2 size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      <EditorDialog
        open={!!edit}
        onOpenChange={(o) => !o && setEdit(null)}
        title={edit?.id ? "Edit Post" : "New Post"}
        data={edit}
        fields={[
          ["title", "Title", "input"],
          ["image_url", "Cover Image URL", "input"],
          ["excerpt", "Excerpt", "textarea"],
          ["content", "Content", "textarea-lg"],
        ]}
        onSave={save}
      />
    </div>
  );
}

/* ============= Messages ============= */
function MessagesAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/messages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/messages/${id}`);
    toast.success("Deleted"); load();
  };
  const markRead = async (id) => {
    await api.put(`/messages/${id}/read`);
    load();
  };
  return (
    <div data-testid="admin-messages">
      <h1 className="font-serif-display text-3xl text-[#1A2456]">Messages</h1>
      <div className="mt-6 space-y-4">
        {items.length === 0 && <p className="text-[#8A817C]">No messages yet.</p>}
        {items.map((m) => (
          <Card key={m.id} data-testid={`msg-${m.id}`} className={`p-6 bg-white border-[#D8CFC4] rounded ${m.is_read ? "opacity-70" : ""}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-serif-display text-lg text-[#1A2456]">{m.name}</h3>
                  {!m.is_read && <Badge className="bg-[#E35D29] text-white">New</Badge>}
                </div>
                <p className="text-sm text-[#5C544F]">{m.email}</p>
                {m.subject && <p className="text-xs tracking-wider uppercase text-[#C24D2C] mt-1">{m.subject}</p>}
              </div>
              <div className="flex gap-2">
                {!m.is_read && (
                  <button data-testid={`msg-read-${m.id}`} onClick={() => markRead(m.id)} className="text-xs text-[#1A2456] link-underline">Mark read</button>
                )}
                <button data-testid={`msg-delete-${m.id}`} onClick={() => remove(m.id)} className="text-[#C24D2C]"><Trash2 size={16} /></button>
              </div>
            </div>
            <p className="mt-4 text-[#2D2825] whitespace-pre-wrap">{m.message}</p>
            <p className="mt-3 text-xs text-[#8A817C]">{new Date(m.created_at).toLocaleString()}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ============= Reusable Editor Dialog ============= */
function EditorDialog({ open, onOpenChange, title, data, fields, onSave }) {
  const [form, setForm] = useState(data || {});
  useEffect(() => { setForm(data || {}); }, [data]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl text-[#1A2456]">{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          {fields.map(([k, l, type]) => (
            <div key={k}>
              <Label>{l}</Label>
              {type === "textarea" && (
                <Textarea data-testid={`field-${k}`} rows={3} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
              {type === "textarea-lg" && (
                <Textarea data-testid={`field-${k}`} rows={10} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
              {type === "input" && (
                <Input data-testid={`field-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              )}
            </div>
          ))}
          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="editor-save" onClick={() => onSave(form)} className="bg-[#E35D29] hover:bg-[#C84B1D] text-white">Save</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
